// Textos que o bot manda no WhatsApp, copiados de backend/server.js
// (handleMinicursoLogic, formatQuestionMessage, checkAndProcessPendingResponse).
// Se o bot mudar o formato, ajuste aqui para a prévia continuar fiel.
import { configDoCurso, ehQuiz, formatarIntervalo, letrasValidas, respostaCorreta } from './util';

// mesma expressão do bot para achar a imagem no texto da pílula
const URL_IMAGEM = /(http(s?):)([/|.|\w|\s|-])*\.(?:jpg|gif|png|jpeg)/g;

// Pílula: imagem (se houver URL no texto) e depois `*titulo*\n\n{conteudo sem a URL}`
export function mensagensDaPilula(pilula) {
  const titulo = pilula.titulo || 'Pílula de conhecimento';
  const conteudo = pilula.conteudo || '';
  const urls = conteudo.match(URL_IMAGEM);
  if (urls && urls.length) {
    const imagem = urls[0];
    const texto = conteudo.replace(imagem, '').trim();
    return [{ imagem }, { texto: texto ? `*${titulo}*\n\n${texto}` : `*${titulo}*` }];
  }
  return [{ texto: `*${titulo}*\n\n${conteudo}` }];
}

export function mensagemDaQuestao(questao) {
  if (!questao || !questao.enunciado) return 'Questão sem enunciado.';
  let msg = `*${questao.enunciado.trim()}*`;
  if (questao.type === 'quiz' || questao.type === 'pesquisa_multipla_escolha') {
    const alternativas = (questao.options || [])
      .map((o, i) => (o && o.text && o.text.trim() !== '' ? `*(${String.fromCharCode(65 + i)})* ${o.text}` : null))
      .filter(Boolean)
      .join('\n\n');
    if (alternativas) {
      msg += `\n\n_Alternativas:_\n\n${alternativas}\n`;
      msg += '\n\n_Responda com a letra da alternativa._';
      return msg;
    }
  }
  return `${msg}\n\n_Por favor, digite sua resposta._`;
}

// Resposta de exemplo do aluno e o retorno do bot (quiz: sempre a alternativa correta)
export function respostaDeExemplo(questao) {
  if (questao.type === 'quiz') {
    const letras = letrasValidas(questao.options);
    if (!letras.length) return null; // o aluno não tem como responder
    const { letra, justificativa } = respostaCorreta(questao.options);
    const acertou = Boolean(letra) && letras.includes(letra);
    const enviada = acertou ? letra : letras[0];
    let retorno = acertou
      ? '✅ Parabéns, resposta correta!'
      : `❌ Resposta incorreta. A correta era: *${letra || 'Não disponível'}*.`;
    if (justificativa) retorno += `\n*Justificativa:* ${justificativa}`;
    return { aluno: enviada, retorno, acertou };
  }
  const multipla = questao.type === 'pesquisa_multipla_escolha' && letrasValidas(questao.options).length;
  return { aluno: multipla ? letrasValidas(questao.options)[0] : 'Resposta do aluno', retorno: 'Obrigado pela sua resposta!', acertou: null };
}

// Conclusão anexada ao retorno da última questão
export function mensagemDeConclusao({ acertos, nQuestoes, aprovacao }) {
  const valorPorQuestao = nQuestoes > 0 ? 10 / nQuestoes : 0;
  const nota = parseFloat((acertos * valorPorQuestao).toFixed(2));
  const corte = Math.ceil((aprovacao / 100) * nQuestoes);
  const aprovado = acertos >= corte;
  let msg = `🎓 *Minicurso Concluído!* 🎓\n\n*Seu desempenho:*\n- Acertos: ${acertos} de ${nQuestoes}\n- Nota Final: ${nota.toFixed(2)} / 10.00\n- Status: *${aprovado ? 'Aprovado' : 'Reprovado'}*`;
  if (!aprovado) msg += '\n\nNão tem problema! 😊 Você pode tentar novamente quando quiser. Basta enviar *Cursos* para recomeçar.';
  return msg;
}

// Acertos mínimos para aprovar (mesma conta do bot)
export const corteDeAprovacao = (aprovacao, nQuestoes) => Math.ceil((aprovacao / 100) * nQuestoes);

// Sequência completa da conversa, na ordem em que o aluno recebe.
// Itens: { tipo: 'aluno'|'bot'|'marco'|'alerta', texto?, imagem?, rotulo?, exemplo? }
export function montarConversa(etapas, curso, { comExemplos = true } = {}) {
  const { intervalo, aprovacao } = configDoCurso(curso);
  const itens = [{ tipo: 'marco', texto: 'O aluno envia o comando (ou escolhe o curso no menu Cursos)' }];
  itens.push({ tipo: 'aluno', texto: curso.comando || 'INICIAR_…' });

  const nQuestoes = etapas.filter((e) => e.conteudo && ehQuiz(e)).length;
  let acertos = 0;

  etapas.forEach((etapa, i) => {
    const n = i + 1;
    const ultima = i === etapas.length - 1;
    if (i > 0) {
      const anterior = etapas[i - 1];
      const depoisDaResposta = anterior.tipoConteudo !== 'pilula';
      itens.push({ tipo: 'marco', texto: `${formatarIntervalo(intervalo)} depois${depoisDaResposta ? ' da resposta' : ''}`, espera: true });
    }
    const conteudo = etapa.conteudo;
    if (conteudo === undefined) {
      itens.push({ tipo: 'bot', rotulo: `Etapa ${n}`, texto: '_Carregando…_' });
      return;
    }
    if (!conteudo) {
      itens.push({ tipo: 'alerta', rotulo: `Etapa ${n}`, texto: 'Conteúdo apagado: o bot não envia nada e o aluno fica parado nesta etapa.' });
      return;
    }
    if (etapa.tipoConteudo === 'pilula') {
      mensagensDaPilula(conteudo).forEach((m, j) => itens.push({ tipo: 'bot', rotulo: j === 0 ? `Etapa ${n} · Pílula` : null, ...m }));
      return;
    }
    const rotulo = conteudo.type === 'quiz' ? 'Quiz' : 'Pesquisa';
    itens.push({ tipo: 'bot', rotulo: `Etapa ${n} · ${rotulo}`, texto: mensagemDaQuestao(conteudo) });

    const exemplo = respostaDeExemplo(conteudo);
    if (!exemplo) {
      itens.push({ tipo: 'alerta', rotulo: `Etapa ${n}`, texto: 'Sem alternativas: o aluno não tem como responder e fica parado aqui.' });
      return;
    }
    if (exemplo.acertou) acertos += 1;
    if (ultima) {
      itens.push({ tipo: 'aluno', texto: exemplo.aluno, exemplo: true });
      itens.push({ tipo: 'bot', texto: `${exemplo.retorno}\n\n${mensagemDeConclusao({ acertos, nQuestoes, aprovacao })}`, final: true });
    } else if (comExemplos) {
      itens.push({ tipo: 'aluno', texto: exemplo.aluno, exemplo: true });
      itens.push({ tipo: 'bot', texto: exemplo.retorno, exemplo: true });
    }
  });

  const fim = etapas[etapas.length - 1];
  if (fim && fim.tipoConteudo === 'pilula') {
    itens.push({ tipo: 'alerta', texto: 'Fim: como a última etapa é uma pílula, o aluno não recebe a mensagem de conclusão com a nota.' });
  }
  return itens;
}
